/**
 * Canonical ChessDesk tier caps and feature matrix.
 * Keep in sync with LicenseTierCatalog.java and legal/LICENSE-TIERS.md.
 * UI labels are i18n keys — resolve with t() in LicenseSettings.
 */

import { getUiLocaleTag } from '../utils/regionalFormat';

export const LICENSE_TIERS = {
  STARTER: { students: 75, branches: 1, coaches: 5, customBranding: false, whatsapp: false, onlinePayments: false, bulkImport: false },
  PROFESSIONAL: {
    students: 250,
    branches: 3,
    coaches: 15,
    customBranding: false,
    whatsapp: true,
    onlinePayments: true,
    bulkImport: false,
  },
  ENTERPRISE: {
    students: 1000,
    branches: 10,
    coaches: 50,
    customBranding: true,
    whatsapp: true,
    onlinePayments: true,
    bulkImport: true,
  },
};

/** Supported commercial plans (display order). */
export const LICENSE_PLANS = [
  { id: 'STARTER', labelKey: 'settings.license.plans.starter', ...LICENSE_TIERS.STARTER },
  { id: 'PROFESSIONAL', labelKey: 'settings.license.plans.professional', ...LICENSE_TIERS.PROFESSIONAL },
  { id: 'ENTERPRISE', labelKey: 'settings.license.plans.enterprise', ...LICENSE_TIERS.ENTERPRISE },
];

/**
 * Feature rows for the admin plan comparison table.
 * `tiers` lists edition ids that include the feature.
 */
export const LICENSE_FEATURE_ROWS = [
  {
    groupKey: 'settings.license.featureGroups.core',
    items: [
      { labelKey: 'settings.license.features.attendance', tiers: ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.billing', tiers: ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.reports', tiers: ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.portal', tiers: ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.email', tiers: ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.atRisk', tiers: ['STARTER', 'PROFESSIONAL', 'ENTERPRISE'] },
    ],
  },
  {
    groupKey: 'settings.license.featureGroups.multiBranch',
    items: [
      { labelKey: 'settings.license.features.multiBranch', tiers: ['PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.managerRole', tiers: ['PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.crossBranch', tiers: ['PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.onlinePayments', tiers: ['PROFESSIONAL', 'ENTERPRISE'] },
      { labelKey: 'settings.license.features.whatsapp', tiers: ['PROFESSIONAL', 'ENTERPRISE'] },
    ],
  },
  {
    groupKey: 'settings.license.featureGroups.enterprise',
    items: [
      { labelKey: 'settings.license.features.bulkImport', tiers: ['ENTERPRISE'] },
      { labelKey: 'settings.license.features.customBranding', tiers: ['ENTERPRISE'] },
    ],
  },
];

export function catalogTierForEdition(edition) {
  if (!edition) return null;
  return LICENSE_TIERS[String(edition).toUpperCase()] ?? null;
}

export function formatTierCaps(tier, t) {
  if (!tier) return '';
  const students = tier.students ?? tier.maxStudents;
  const branches = tier.branches ?? tier.maxBranches;
  const coaches = tier.coaches ?? tier.maxCoaches;
  if (students == null) return '';
  const locale = getUiLocaleTag();
  const studentsFmt = Number(students).toLocaleString(locale);
  const translate = typeof t === 'function'
    ? t
    : (key, opts) => opts?.defaultValue || key;
  return translate('settings.license.tierCaps', {
    students: studentsFmt,
    branches,
    coaches,
    defaultValue: 'up to {{students}} students, {{branches}} branches, {{coaches}} coaches',
  });
}

export function planIncludesFeature(planId, tiers) {
  return tiers.includes(String(planId).toUpperCase());
}

export function planLabel(plan, t) {
  if (!plan) return '';
  const defaults = { STARTER: 'Starter', PROFESSIONAL: 'Professional', ENTERPRISE: 'Enterprise' };
  return t(plan.labelKey || `settings.license.plans.${String(plan.id).toLowerCase()}`, {
    defaultValue: defaults[plan.id] || plan.id,
  });
}
